import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { useMe } from "@/api/me";
import type {
  BillingAgreementView,
  BillingCatalog,
  BillingInvoiceFilters,
  BillingInvoiceList,
  BillingSummary,
} from "@/api/types";

/** Serialise report filters into a query string, dropping empty and
 *  null values. A blank `status=` would filter on the empty string and
 *  return nothing — indistinguishable, in the UI, from "no invoices". */
export function buildBillingInvoiceQuery(
  filters: BillingInvoiceFilters,
  extra?: Record<string, string>,
): string {
  const params = new URLSearchParams();
  const entries: [string, string | null | undefined][] = [
    ["org_id", filters.org_id],
    ["status", filters.status],
    ["provider", filters.provider],
    ["currency", filters.currency],
    ["from", filters.from],
    ["to", filters.to],
  ];
  for (const [key, value] of entries) {
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, value);
    }
  }
  for (const [key, value] of Object.entries(extra ?? {})) params.set(key, value);
  return params.toString();
}

/** Invoice report. Server-side scoped by the session's pinned tenant,
 *  so the active org slug joins the queryKey exactly like the ledger
 *  summary — rapid org-switching must not serve another tenant's rows
 *  from cache before the refetch lands. */
export function useAdminBillingInvoices(filters: BillingInvoiceFilters) {
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  const query = buildBillingInvoiceQuery(filters);
  return useQuery({
    queryKey: ["admin", "billing", "invoices", activeOrgSlug, query],
    queryFn: async () =>
      (
        await api.get<BillingInvoiceList>(
          query ? `/admin/billing/invoices?${query}` : "/admin/billing/invoices",
        )
      ).data,
    staleTime: 30_000,
  });
}

/** Revenue rollup. `currencies` is one bucket per SEK/EUR/USD and
 *  carries NO combined total — see the backend's reports module. */
export function useAdminBillingSummary(from: string | null, to: string | null) {
  const activeOrgSlug = useMe().data?.admin_scope?.active_org_slug ?? null;
  const query = buildBillingInvoiceQuery({ from, to });
  return useQuery({
    queryKey: ["admin", "billing", "summary", activeOrgSlug, query],
    queryFn: async () =>
      (
        await api.get<BillingSummary>(
          query ? `/admin/billing/summary?${query}` : "/admin/billing/summary",
        )
      ).data,
    staleTime: 30_000,
  });
}

/** CSV export. Fetched as a blob through the same axios instance so
 *  the auth cookie and CSRF plumbing apply; a bare `<a href>` would
 *  bypass the interceptors and 401 silently. */
export function useDownloadBillingInvoicesCsv() {
  return useMutation({
    mutationFn: async (filters: BillingInvoiceFilters) => {
      const query = buildBillingInvoiceQuery(filters, { format: "csv" });
      const resp = await api.get<Blob>(`/admin/billing/invoices?${query}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `invoices-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      // Revoke on the next tick: revoking synchronously can cancel the
      // download in some browsers before it has started reading.
      setTimeout(() => URL.revokeObjectURL(url), 0);
      return true;
    },
  });
}

/** VAT report CSV — the accountant's periodic sales/VAT export.
 *  Same blob approach as the invoices CSV so cookies + CSRF apply.
 *  The endpoint REQUIRES a period; callers gate on from+to. */
export function useDownloadVatReportCsv() {
  return useMutation({
    mutationFn: async ({ from, to }: { from: string; to: string }) => {
      const query = new URLSearchParams({ from, to, format: "csv" });
      const resp = await api.get<Blob>(
        `/admin/billing/vat-report?${query.toString()}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `vat-report-${from.slice(0, 10)}-${to.slice(0, 10)}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 0);
      return true;
    },
  });
}

/** Render (or re-render) a MANUAL invoice's branded PDF.
 *
 *  Superadmin-gated server-side. Invalidates the billing queries so
 *  the freshly written `document_id` shows up in the table without a
 *  manual refresh. Stripe invoices are refused with 409 — they host
 *  their own PDF and the UI never offers the action for them. */
export function useRenderInvoiceDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string) =>
      (
        await api.post<{ document_id: string }>(
          `/admin/billing/invoices/${invoiceId}/document`,
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}

/** The result of recording payment of a manual invoice. The delta is
 *  the signed subscription-bucket micros the reset applied — 0 when
 *  the invoice carries no agreement (a one-off credit pack) or when
 *  the period was already refilled, because the reset is idempotent
 *  on `(agreement_id, period_start)` in the database.
 *
 *  `tax_minor` and `total_minor` come back so the caller can see the
 *  recomputed header without a refetch; both are integer minor units. */
export type MarkPaidResult = {
  invoice_id: string;
  status: string;
  paid_at: string;
  tax_minor: number;
  total_minor: number;
  subscription_delta_micros: number;
};

/** Record payment of a MANUAL invoice.
 *
 *  This is the manual provider's half of "credits follow money": the
 *  route fires the same `reset_subscription_bucket` a Stripe
 *  `invoice.paid` fires. Superadmin-gated server-side, and refused
 *  with 409 for a Stripe invoice — the UI never offers it there.
 *
 *  `paid_at` is not sent: the operator is recording "it landed", and
 *  the backend stamps now (UTC). Back-dating is a `paid_at` the API
 *  accepts but this screen does not yet ask for.
 *
 *  `tax_minor` is INTEGER minor units and is omitted from the body
 *  entirely when the caller has none, because the route reads a
 *  missing field as "leave the stored tax alone" and an explicit `0`
 *  as "the tax is zero". Sending `0` for "the operator typed nothing"
 *  would silently wipe a tax somebody already recorded. */
export function useMarkInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { invoiceId: string; tax_minor?: number }) =>
      (
        await api.post<MarkPaidResult>(
          `/admin/billing/invoices/${vars.invoiceId}/paid`,
          vars.tax_minor === undefined ? {} : { tax_minor: vars.tax_minor },
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}

/** The plan catalog, including inactive rows so an operator can see
 *  and activate the parked SEK/USD placeholders. Not org-scoped — the
 *  catalog is platform-wide. */
export function useAdminBillingPlans(includeInactive = true) {
  return useQuery({
    queryKey: ["admin", "billing", "plans", includeInactive],
    queryFn: async () =>
      (
        await api.get<BillingCatalog>(
          `/admin/billing/plans?include_inactive=${includeInactive}`,
        )
      ).data,
    staleTime: 30_000,
  });
}

/** Put an org on a plan when it has NO agreement yet.
 *
 *  The panel could change, re-seat and cancel an existing agreement but
 *  never create one, so the single operation the runbook tells an
 *  operator to perform before enabling billing — assigning "Beta" to
 *  each existing customer so their team does not freeze — had no UI at
 *  all and had to be done with curl.
 *
 *  ``provider`` is the caller's, because an INTERNAL plan is refused
 *  with 400 internal_plan_not_billable on "stripe": internal means not
 *  Stripe-connected, and asking for both is a contradiction. */
export function useCreateOrgAgreement(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      plan_slug: string;
      currency: string;
      provider: "manual" | "stripe";
      seat_quantity: number;
      start_now: boolean;
    }) => (await api.post(`/admin/billing/orgs/${orgId}/agreement`, body)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "billing", "agreement", orgId] });
      qc.invalidateQueries({ queryKey: ["admin", "billing"] });
    },
  });
}

/** Upsert one (plan, currency) price. `unit_amount_minor` MUST already
 *  be an integer — the caller validates, because a float that reaches
 *  here is a 422 from the backend and a rounding decision nobody made. */
export function useUpsertPlanPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      planId: string;
      currency: string;
      unit_amount_minor: number;
      stripe_price_id: string | null;
      is_active: boolean;
    }) =>
      (
        await api.put(
          `/admin/billing/plans/${vars.planId}/prices/${vars.currency}`,
          {
            unit_amount_minor: vars.unit_amount_minor,
            stripe_price_id: vars.stripe_price_id,
            is_active: vars.is_active,
          },
        )
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}

/** Schedule every live subscriber of a plan (or seat product) in a
 *  currency onto today's list price — at each one's first renewal at
 *  least 30 days out, with notice now. Never immediate. */
export function useMoveSubscribersToPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { planId: string; currency: string }) =>
      (
        await api.post<{
          scheduled: { agreement_id: string; org_id: string; requested_at: string }[];
          skipped: { agreement_id: string; org_id: string; reason: string }[];
          notice_days: number;
        }>(`/admin/billing/plans/${vars.planId}/prices/${vars.currency}/move-subscribers`, {})
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}

/** One org's move to today's list price: schedule (true) or withdraw (false). */
export function useOrgPriceChange(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (schedule: boolean) =>
      (schedule
        ? await api.post(`/admin/billing/orgs/${orgId}/agreement/price-change`, {})
        : await api.delete(`/admin/billing/orgs/${orgId}/agreement/price-change`)
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}

/** The org's live agreement plus its measured activity. Disabled when
 *  no org is selected — the routes are keyed by org id and there is no
 *  sensible cross-org answer. */
export function useAdminOrgAgreement(orgId: string | null) {
  return useQuery({
    queryKey: ["admin", "billing", "agreement", orgId],
    queryFn: async () =>
      (await api.get<BillingAgreementView>(`/admin/billing/orgs/${orgId}/agreement`))
        .data,
    enabled: orgId !== null,
    staleTime: 15_000,
  });
}

/** Change the org's plan. `immediate: null` lets the backend derive
 *  the direction from the allowances — more credits is an upgrade
 *  (immediate + prorated), fewer is a downgrade (scheduled to the
 *  period boundary). Overriding it here would let an operator schedule
 *  an upgrade or force a downgrade mid-period by accident. */
export function useChangeOrgPlan(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { plan_slug: string; immediate: boolean | null }) =>
      (
        await api.post(`/admin/billing/orgs/${orgId}/agreement/plan`, {
          plan_slug: vars.plan_slug,
          immediate: vars.immediate,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}

/** Renew an INTERNAL plan (Beta, Free, Free trial): restart the period
 *  now and reset the included credits to the plan's allowance. The
 *  backend refuses Stripe-billed agreements and paid plans with the
 *  same codes as a plan change. `renewed: false` is a double click the
 *  backend absorbed — nothing changed, and `allowance_micros` is null. */
export function useRenewOrgAgreement(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          agreement: BillingAgreementView["agreement"];
          renewed: boolean;
          allowance_micros: number | null;
        }>(`/admin/billing/orgs/${orgId}/agreement/renew`, {})
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}

/** Change the DECLARED seat quantity. Measured activity is never sent. */
export function useChangeOrgSeats(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (quantity: number) =>
      (
        await api.post(`/admin/billing/orgs/${orgId}/agreement/seats`, { quantity })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}

/** Cancel. `at_period_end: true` leaves the org its full allowance
 *  until the boundary; `false` zeroes the subscription bucket there and
 *  then. The purchased bucket is never touched either way — those
 *  credits are the customer's property, not a subscription benefit. */
export type BillingEventRow = {
  id: string;
  provider: string;
  provider_event_id: string;
  type: string;
  received_at: string | null;
  processed_at: string | null;
  next_attempt_at: string | null;
  attempts: number;
  error: string | null;
  stuck: boolean;
};

export type BillingEventsPage = {
  events: BillingEventRow[];
  stuck_total: number;
  stuck_after_attempts: number;
  limit: number;
  offset: number;
};

/** Stripe deliveries and what became of them.
 *
 *  The webhook route stores every verified delivery and 200s BEFORE the
 *  handler runs, so Stripe's own dashboard reports 100% delivered
 *  whether or not entitlement was ever applied. Stripe will never tell
 *  you a customer paid and got nothing; this is where that shows up.
 *
 *  Polled rather than fetched once: an operator leaves this open while
 *  working through a backlog, and a stale count is the one number on
 *  the page that must not be stale. */
export function useBillingEvents(state: string, enabled = true) {
  return useQuery({
    queryKey: ["admin", "billing", "events", state],
    enabled,
    refetchInterval: 30_000,
    queryFn: async () =>
      (
        await api.get<BillingEventsPage>(
          `/admin/billing/events?state=${encodeURIComponent(state)}`,
        )
      ).data,
  });
}

/** Clear the backoff so the loop retries this delivery on its next tick.
 *  Deliberately not a dispatch: the handler grants credits and writes
 *  invoices, and running it on a request thread would put that outside
 *  the loop's row lock — the thing stopping a delivery being applied
 *  twice. */
export function useRetryBillingEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (eventId: string) =>
      (await api.post(`/admin/billing/events/${eventId}/retry`, {})).data,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["admin", "billing", "events"] }),
  });
}

export type RefundMode = "none" | "unused" | "full";

/** What an immediate cancel would refund (Stripe's credit note preview
 *  for a Stripe agreement), VAT included. */
export type RefundQuote = {
  mode: Exclude<RefundMode, "none">;
  provider: string;
  currency: string;
  invoice_number: string;
  paid_total_minor: number;
  already_refunded_minor: number;
  net_minor: number;
  tax_minor: number;
  total_minor: number;
  as_of: string;
  /** A refund was already made on this invoice for this agreement (an
   *  earlier attempt whose cancel failed): confirming only cancels. */
  already_refunded?: boolean;
  credit_note_number?: string | null;
};

/** Why an operator ends a plan: at the customer's request (the org falls
 *  back to Free, or Beta) or a termination for misuse or non-payment (Not
 *  enrolled). */
export type CancelCause = "operator_cancel" | "operator_termination";

export type CancelArgs = {
  atPeriodEnd: boolean;
  refund?: RefundMode;
  cause?: CancelCause;
  /** The confirmed quote's moment and total; the server refuses if the
   *  refund would now be a different amount. */
  quoteAsOf?: string;
  expectedTotalMinor?: number;
};

export function useRefundQuote(
  orgId: string | null,
  mode: RefundMode,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["admin", "billing", "refund-quote", orgId, mode],
    enabled: enabled && orgId !== null && mode !== "none",
    // A quote is a moment's figure; never show a cached one.
    gcTime: 0,
    staleTime: 0,
    retry: false,
    queryFn: async () =>
      (
        await api.get<{ quote: RefundQuote }>(
          `/admin/billing/orgs/${orgId}/agreement/refund-quote`,
          { params: { mode } },
        )
      ).data.quote,
  });
}

export function useCancelOrgAgreement(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: boolean | CancelArgs) => {
      const opts: CancelArgs = typeof args === "boolean" ? { atPeriodEnd: args } : args;
      return (
        await api.post(`/admin/billing/orgs/${orgId}/agreement/cancel`, {
          at_period_end: opts.atPeriodEnd,
          ...(opts.cause ? { cause: opts.cause } : {}),
          ...(opts.refund && opts.refund !== "none"
            ? {
                refund: opts.refund,
                quote_as_of: opts.quoteAsOf,
                expected_total_minor: opts.expectedTotalMinor,
              }
            : {}),
        })
      ).data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "billing"] }),
  });
}
